import { Component, OnInit, ViewChild} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '../../core/models';
import { AppState } from '../../app-state';
import { Store as NgrxStore } from '@ngrx/store';
import { DetailResetAction } from '../detail-actions';
import { AuthorizationAwareComponent } from '../../core/authorization-aware-component';
import { StoreDetailService } from '../../core/store/store-detail.service';
import { DetailState, selectDetailState, selectvolumeDetailState } from '../detail-state';
import { combineLatest } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { LicenseType } from '../../auth/authorization/license-type.enum';
import { ClipboardUtil } from '../../core/util/clipboard-util';
import { ToastService } from '../../core/toast/toast.service';
import { PdfExportService } from '../../core/service/pdf-export.service';
import { DynamicPdfService, StoreHeaderData, MetricItem, AlertItem, SalesPerformanceData, DemographicData, BannerAnalysisData, AdditionalInsightsData, StoreRankTableData, StoreRankTableRow } from '../../core/service/dynamic-pdf.service';
import { StoreDetailPdfFirstPageComponent } from './store-detail-pdf-first-page/store-detail-pdf-first-page.component';
import { StoreDetailMiddlePageComponent } from './store-detail-middle-page/store-detail-middle-page.component';
import { HighlightsDataService } from '../../core/services/highlights-data.service';
import { SalesPerformanceDataService } from '../../core/services/sales-performance-data.service';
import { DemographicDataService } from '../../core/services/demographic-data.service';
import { BannerAnalysisDataService } from '../../core/services/banner-analysis-data.service';
import { AdditionalInsightsDataService } from '../../core/services/additional-insights-data.service';
import { StoreVolume } from '../../core/store/volume/store-volume';
import { formatAsCurrency } from '../../core/util/string-utils';
import * as _ from 'lodash';
import { ActiveStoreStatuses, FutureStoreStatuses, HistoricalStoreStatuses } from '../../core/identity/constant/store-status-type.enum';
import { LogoUtil } from 'src/app/core/util/logo-util';
import { selectUserState, UserState } from '../../auth/user-state';
import { SalesVolumeDisplayType } from '../../core/user-preferences/sales-volume-display-type.enum';
import { RankingFilters } from './store-sales-performance-panel/store-ranking-filters/store-ranking-filters.component';
//import { StoreRanking } from '../../core/store/ranking/store-ranking';
// import { StoreRankingService } from '../../core/store/ranking/store-ranking.service';
// import { StoreComparisonService } from '../../core/store/ranking/store-comparison.service';
// import { ComparisonType } from '../../core/store/ranking/comparison-type.enum';
// import { RegionType } from '../../core/location/region-type.enum';

@Component({
  standalone: false,
  selector: 'mtn-store-detail-page',
  templateUrl: './store-detail-page.component.html',
  styleUrls: ['./store-detail-page.component.scss']
})
export class StoreDetailPageComponent extends AuthorizationAwareComponent implements OnInit {

  @ViewChild('pdfFirstPage') pdfFirstPageComponent: StoreDetailPdfFirstPageComponent;
  @ViewChild('pdfMiddlePage') pdfMiddlePageComponent: StoreDetailMiddlePageComponent;
  @ViewChild('pdfDynamicPage') pdfDynamicPageComponent: any;

  isMarketAreaLoaded = false;
  isPdfDataReady = false;
  licenseLevel: LicenseType;
  store: Store;
  showComprehensiveRanking = false;
  criticalInsights: any = {};
  salesPerformance: any = {};
  additionalInsights: any = {};
  showPdfPreview = false;
  isCriticalInsightsUIReady = false;
  salesVolumeDisplayMode: SalesVolumeDisplayType = SalesVolumeDisplayType.WEEKLY;

  // Data for dynamic PDF
  private volume: StoreVolume;
  private highlights: AlertItem[] = [];
  private salesPerformanceData: SalesPerformanceData = { byVolume: [], bySqft: [], selectedView: 'volume' };
  private demographicData: DemographicData;
  private bannerAnalysisData: BannerAnalysisData;
  private additionalInsightsData: AdditionalInsightsData;
  private cardTitleStatus: string = 'Unknown';

  // Selected key indicator for PDF (volume or sqft)
  selectedKeyIndicatorForPdf: 'volume' | 'sqft' = 'volume';

  private isLoading = false;
  private uuid: string;

  constructor(protected ngrxStore: NgrxStore<AppState>,
              private route: ActivatedRoute,
              private storeDetailService: StoreDetailService,
              private toaster: ToastService,
              private pdfExportService: PdfExportService,
              private dynamicPdfService: DynamicPdfService,
              private highlightsDataService: HighlightsDataService,
              private salesPerformanceDataService: SalesPerformanceDataService,
              private demographicDataService: DemographicDataService,
              private bannerAnalysisDataService: BannerAnalysisDataService,
              private additionalInsightsDataService: AdditionalInsightsDataService) {
    super(ngrxStore);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  onAuthorizationInit(): void {
    this.licenseLevel = this.currentUser.licenseType;
    this.subscribeToUserPreferences();
    this.subscribeToRouteChanges();
    this.subscribeToDetailState();
    this.subscribeToVolumeState();
    this.subscribeToPdfDataServices();
  }

  onAuthorizationChange(): void {

  }

  private subscribeToUserPreferences(): void {
    this.addSubscription(
      selectUserState(this.ngrxStore)
        .subscribe((state: UserState) => {
          if (state.userPreferences) {
            this.salesVolumeDisplayMode = state.userPreferences.salesVolumeDisplayMode || SalesVolumeDisplayType.WEEKLY;
          }
        })
    );
  }

  private subscribeToVolumeState(): void {
    this.addSubscription(
      selectvolumeDetailState(this.ngrxStore)
        .subscribe((volume: StoreVolume) => {
          this.volume = volume;
        })
    );
  }

  private subscribeToPdfDataServices(): void {
    // Subscribe to highlights
    this.addSubscription(
      this.highlightsDataService.highlights$.subscribe((highlights: any[]) => {
        console.log("🚀 ~ StoreDetailPageComponent ~ subscribeToPdfDataServices ~ highlights:", highlights)
        this.highlights = highlights.map(h => ({
          title: h.title,
          description: h.description,
          isPositive: h.isPositive
        }));
      })
    );

    // Subscribe to sales performance
    this.addSubscription(
      this.salesPerformanceDataService.salesPerformance$.subscribe((data: any) => {
        this.salesPerformanceData = {
          byVolume: (data.byVolume || []).map((item: any) => ({
            percentile: item.percentile || 0,
            status: item.status || '',
            description: item.description || '',
            color: item.color || '#666666'
          })),
          bySqft: (data.bySqft || []).map((item: any) => ({
            percentile: item.percentile || 0,
            status: item.status || '',
            description: item.description || '',
            color: item.color || '#666666'
          }))
        };
      })
    );

    // Subscribe to demographic data
    this.addSubscription(
      this.demographicDataService.demographicData$.subscribe((data: any) => {
        if (data.demographics) {
          const demographics = data.demographics;
          let regionText = '7 minutes Drive Time';
          if (data.regionType === 'DRIVE_TIME') {
            regionText = `${data.regionValue} Drive Time`;
          } else if (data.regionType === 'RING') {
            regionText = `${data.regionValue} Ring`;
          } else {
            regionText = 'Market Area';
          }

          this.demographicData = {
            ethnicity: {
              asian: this.round(demographics.ethnicityAsian * 100, 1),
              hispanic: this.round(demographics.ethnicityHispanic * 100, 1),
              black: this.round(demographics.ethnicityBlack * 100, 1),
              white: this.round(demographics.ethnicityWhite * 100, 1),
              other: this.round(demographics.ethnicityOther * 100, 1)
            },
            population: {
              total: demographics.populationEstimate || 0,
              growth2yr: this.round(demographics.populationHistoricalGrowth2Year * 100, 2),
              projected5yr: this.round(demographics.populationProjectedGrowth5Year * 100, 2)
            },
            people: {
              averageAge: demographics.ageAverage || 0,
              collegeEducated: this.round(demographics.educationCollegeOrDegree * 100, 2),
              householdSize: demographics.householdsPersonsPer || 0
            },
            income: {
              average: this.round(demographics.householdsAverageIncome, 0),
              median: demographics.householdsMedianIncome || 0
            },
            regionText
          };
        }
      })
    );

    // Subscribe to banner analysis
    this.addSubscription(
      this.bannerAnalysisDataService.bannerAnalysis$.subscribe((data: any) => {
        this.bannerAnalysisData = {
          bannerName: data.bannerName || '',
          marketName: data.marketName || '',
          salesSqft: data.salesSqftData ? {
            percentile: data.salesSqftData.percentile || 0,
            status: data.salesSqftData.status || '',
            description: data.salesSqftData.description || '',
            color: data.salesSqftData.color || '#666666',
            prefix: 'This Banner is a'
          } : undefined,
          salesVolume: data.salesVolumeData ? {
            percentile: data.salesVolumeData.percentile || 0,
            status: data.salesVolumeData.status || '',
            description: data.salesVolumeData.description || '',
            color: data.salesVolumeData.color || '#666666',
            prefix: 'This Banner is a'
          } : undefined,
          storeCount: data.storeCountData ? {
            percentile: data.storeCountData.percentile || 0,
            status: data.storeCountData.status || '',
            description: data.storeCountData.description || '',
            color: data.storeCountData.color || '#666666',
            prefix: 'This Banner is a'
          } : undefined
        };
      })
    );

    // Subscribe to additional insights
    this.addSubscription(
      this.additionalInsightsDataService.additionalInsights$.subscribe((data: any) => {
        const services = data.services;
        const ratings = data.ratings;

        const availableServices: string[] = [];
        const availableAmenities: string[] = [];

        if (services) {
          if (services.bakery) availableServices.push('Bakery');
          if (services.deli) availableServices.push('Deli');
          if (services.meat) availableServices.push('Meat');
          if (services.seafood) availableServices.push('Seafood');
          if (services.pharmacy) availableServices.push('Pharmacy');
          if (services.floral) availableServices.push('Floral');
          if (services.cheese) availableServices.push('Cheese');

          if (services.wine) availableAmenities.push('Wine');
          if (services.sushi) availableAmenities.push('Sushi');
          if (services.seating) availableAmenities.push('Seating Area');
          if (services.saladBar) availableAmenities.push('Salad Bar');
          if (services.onlinePickup) availableAmenities.push('Online Pickup');
          if (services.oliveBar) availableAmenities.push('Olive Bar');
          if (services.liquor) availableAmenities.push('Liquor');
          if (services.inStoreRestaurant) availableAmenities.push('In-Store Restaurant');
          if (services.fuel) availableAmenities.push('Fuel');
          if (services.extensivePreparedFoods) availableAmenities.push('Extensive Prepared Foods');
          if (services.expandedGM) availableAmenities.push('Expanded GM');
          if (services.coffee) availableAmenities.push('Coffee Shop');
          if (services.bulk) availableAmenities.push('Bulk');
          if (services.beer) availableAmenities.push('Beer');
          if (services.bank) availableAmenities.push('Bank');
        }

        const formatRating = (value: number) => value ? `${value}/5` : 'NA';
        const getRating = (type: string) => ratings?.getRatingOfType?.(type)?.value;

        this.additionalInsightsData = {
          servicesCount: availableServices.length + availableAmenities.length,
          services: availableServices,
          amenities: availableAmenities,
          analystRatingDate: ratings?.ratingDate ? new Date(ratings.ratingDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '',
          storeRatings: [
            [{ name: 'Ceiling Condition', rating: formatRating(getRating('STORE_CEILING_CONDITION')) },
             { name: 'Checkstand Condition', rating: formatRating(getRating('STORE_CHECKSTAND_CONDITION')) }],
            [{ name: 'Exterior Condition', rating: formatRating(getRating('STORE_EXTERIOR_CONDITION')) },
             { name: 'Floor Condition', rating: formatRating(getRating('STORE_FLOOR_CONDITION')) }],
            [{ name: 'Refrigeration Condition', rating: formatRating(getRating('STORE_REFRIGERATION_CONDITION')) },
             { name: 'Shelving Condition', rating: formatRating(getRating('STORE_SHELVING_CONDITION')) }],
            [{ name: 'Visibility from Road', rating: formatRating(getRating('STORE_VISIBILITY')) },
             { name: 'Wall Condition', rating: formatRating(getRating('STORE_WALL_CONDITION')) }]
          ],
          parkingLotRatings: [
            [{ name: 'General Condition', rating: formatRating(getRating('PARKING_LOT_CONDITION')) },
             { name: 'Ingress/Egress', rating: formatRating(getRating('PARKING_LOT_INGRESS_EGRESS')) }],
            [{ name: 'Lighting', rating: formatRating(getRating('PARKING_LOT_LIGHTING')) },
             { name: 'Traffic Flow', rating: formatRating(getRating('PARKING_LOT_FLOW')) }]
          ],
          cotenantRatings: [
            [{ name: 'Exterior Condition', rating: formatRating(getRating('COTENANT_EXTERIOR_CONDITION')) },
             { name: 'Synergy', rating: formatRating(getRating('COTENANT_SYNERGY')) }]
          ]
        };
      })
    );
  }

  private round(value: number, decimals: number): number {
    if (!value) return 0;
    return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }

  copyUrlToClipboard(): void {
    ClipboardUtil.copyValueToClipboard(window.location.href);
    this.toaster.info("Copied URL to Clipboard");
  }

  previewPdf(): void {
    this.showPdfPreview = true;
  }

  closePdfPreview(): void {
    this.showPdfPreview = false;
  }

  async downloadPdf(): Promise<void> {
    // Use the new dynamic PDF generation
    await this.downloadDynamicPdf();
  }

  /**
   * Download PDF using the new dynamic PDF service (native jsPDF elements)
   * This is faster and produces better quality PDFs
   */
  async downloadDynamicPdf(): Promise<void> {
    try {
      this.toaster.info('Generating PDF... Please wait.');

      const filename = `store-details-${this.store?.banner?.name || 'report'}-${new Date().toISOString().split('T')[0]}.pdf`;
    let url = LogoUtil.buildLogoUrl(this.store.banner.logoCloudinaryFilename);
    url = url.replace('h_20,w_100', 'h_40,w_200'); //Allow these to be larger than default

      // Prepare header data
      const headerData: StoreHeaderData = {
        storeName: this.store?.name || this.store?.banner?.name || 'Store',
        storeNumber: this.store?.number || '',
        bannerName: this.store?.banner?.name || 'Unknown Banner',
        fit: this.store?.fit || 'Unknown FIT',
        address: this.store?.space?.location?.addressLine1 || 'Address Not Available',
        cityStateZip: this.getFormattedAddress(),
        shoppingCenter: this.store?.space?.shoppingCenter?.name || 'Unnamed Shopping Center',
        shoppingCenterOwner: this.store?.space?.shoppingCenter?.ownerCompany?.name || 'Unknown Owner',
        shoppingCenterType: this.store?.space?.shoppingCenter?.type || 'Unknown Type',
        logoUrl:url
      };

      // Prepare metrics data
      const metrics: MetricItem[] = this.buildMetricsData();

      // Get store status color
      const statusColor = this.getStatusColor();
      if (metrics[3]) {
        metrics[3].color = statusColor;
      }

      // Prepare default demographic data if not available
      const demographics = this.demographicData || {
        ethnicity: { asian: 0, hispanic: 0, black: 0, white: 0, other: 0 },
        population: { total: 0, growth2yr: 0, projected5yr: 0 },
        people: { averageAge: 0, collegeEducated: 0, householdSize: 0 },
        income: { average: 0, median: 0 },
        regionText: '7 minutes Drive Time'
      };

      // Prepare default banner analysis data if not available
      const bannerAnalysis = this.bannerAnalysisData || {
        bannerName: this.store?.banner?.name || '',
        marketName: 'Market Area'
      };

      // Prepare default additional insights data if not available
      const additionalInsights = this.additionalInsightsData || {
        servicesCount: 0,
        services: [],
        amenities: [],
        analystRatingDate: '',
        storeRatings: [],
        parkingLotRatings: [],
        cotenantRatings: []
      };

      // Update sales performance data with selected view
      const salesPerformance: SalesPerformanceData = {
        ...this.salesPerformanceData,
        selectedView: this.selectedKeyIndicatorForPdf
      };

      // Build store rank table data (dynamic API data)
      const storeRankTable = await this.buildStoreRankTableData();

      // Generate PDF using the dynamic service
      await this.dynamicPdfService.generateStoreDetailPdf(
        headerData,
        metrics,
        this.highlights,
        salesPerformance,
        demographics,
        bannerAnalysis,
        additionalInsights,
        filename,
        storeRankTable
      );

      this.toaster.info('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      this.toaster.error(`Failed to generate PDF: ${error.message}`);
    }
  }

  /**
   * Download PDF using the old html2canvas approach (for comparison/fallback)
   */
  async downloadPdfLegacy(): Promise<void> {
    try {
      this.toaster.info('Generating PDF (legacy)... Please wait.');
      const filename = `store-details-${this.store?.banner?.name || 'report'}-${new Date().toISOString().split('T')[0]}.pdf`;

      const pageSelectors = ['mtn-store-detail-pdf-first-page'];
      const insightsCount = this.pdfFirstPageComponent?.getCriticalInsightsCount() || 0;

      if (insightsCount > 10) {
        pageSelectors.push('mtn-store-detail-dynamic-page');
      }

      pageSelectors.push('mtn-store-detail-middle-page');
      pageSelectors.push('mtn-store-detail-pdf-second-page');

      const pages = pageSelectors.map(selector => [selector]);
      await this.pdfExportService.exportToPdfWithPages(pages, filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      this.toaster.error(`Failed to generate PDF: ${error.message}`);
    }
  }

  private buildMetricsData(): MetricItem[] {
    const metrics: MetricItem[] = [];
    const displaySuffix = this.salesVolumeDisplayMode === SalesVolumeDisplayType.ANNUAL ? 'Annually' : 'Weekly';
    console.log("🚀 ~ StoreDetailPageComponent ~ buildMetricsData ~ displaySuffix:", displaySuffix)

    // Get display mode text based on user preference
    const displayMode = this.userPreferences.salesVolumeDisplayMode === SalesVolumeDisplayType.ANNUAL ? 'Annually' : 'Weekly';

    // Sales Volume
    if (this.volume?.total) {
      const dateStr = this.volume.date ? new Date(this.volume.date).toLocaleDateString('en-US', {month: '2-digit', year: 'numeric'}) : '';
      metrics.push({
        value: formatAsCurrency(this.volume.total, false),
        label: `Est. Sales Volume (${displayMode}) ${dateStr}`
      });
    } else {
      metrics.push({
        value: this.volume ? 'Not Purchased' : 'Not Available',
        label: `Est. Sales Volume (${displayMode})`
      });
    }

    // Sales per SQFT
    if (this.volume?.salesSqft) {
      metrics.push({
        value: `${formatAsCurrency(this.volume.salesSqft)} /sqft`,
        label: `Est. Sales/SQFT (${displayMode}, Total Area)`
      });
    } else {
      metrics.push({
        value: this.volume ? 'Not Purchased' : 'Not Available',
        label: `Est. Sales/SQFT (${displayMode}, Total Area)`
      });
    }

    // Sales Area
    metrics.push({
      value: `${this.store?.grocerySalesArea?.toLocaleString('en-US') || 'Unknown'} / ${this.store?.totalArea?.toLocaleString('en-US') || 'Unknown'} sqft`,
      label: 'Sales / Total Area'
    });

    // Store Status
    metrics.push({
      value: this.cardTitleStatus || 'Unknown',
      label: 'Store Status'
    });

    return metrics;
  }

  private getFormattedAddress(): string {
    if (!this.store?.space?.location) return '';
    const location = this.store.space.location;
    const parts = [];
    if (location.city) parts.push(location.city + ',');
    if (location.state) parts.push(location.state);
    if (location.postalCode) parts.push(location.postalCode);
    return parts.join(' ');
  }

  private getStatusColor(): string {
    if (!this.store) return '#32CD32'; // limegreen

    const status = this.store.getCurrentStatus?.();
    if (!status) return '#32CD32';

    const statusName = status.getStatusSystemName?.();
    if (!statusName) return '#32CD32';

    if (_.includes(FutureStoreStatuses, statusName)) {
      return '#1E90FF'; // dodgerblue
    } else if (_.includes(ActiveStoreStatuses, statusName)) {
      return '#32CD32'; // limegreen
    } else if (_.includes(HistoricalStoreStatuses, statusName)) {
      return '#FF0000'; // red
    }

    return '#32CD32';
  }

  /**
   * Update selected key indicator for PDF export
   * Called from the sales performance panel when user changes the view
   */
  updateSelectedKeyIndicator(indicator: 'volume' | 'sqft'): void {
    this.selectedKeyIndicatorForPdf = indicator;
  }

  // Store current ranking data to avoid duplicate API calls
  private currentRankingData: any[] = [];

  /**
   * Update current ranking filters for PDF generation
   */
  updateCurrentRankingFilters(filters: RankingFilters): void {
    // Store filters for potential future use
    console.log(filters);
  }

  /**
   * Update current ranking data from scatter chart component
   */
  updateCurrentRankingData(data: any[]): void {
    this.currentRankingData = data || [];
  }

  /**
   * Build store rank table data using existing ranking data (no new API call)
   */
  private async buildStoreRankTableData(): Promise<StoreRankTableData> {
    try {
      // Always use existing ranking data if available to avoid duplicate API calls
      if (this.currentRankingData && this.currentRankingData.length > 0) {
        const detailState = await selectDetailState(this.ngrxStore).pipe(take(1)).toPromise();

        if (!detailState?.store) {
          return this.getStaticStoreRankTableData();
        }

        const rankings = this.currentRankingData;
        const currentStoreRanking = rankings.find(r => r.store?.uuid === detailState.store.uuid);

        // Convert all rankings to table rows
        const rows: StoreRankTableRow[] = rankings.map(ranking => {
          const isCurrentStore = ranking.store?.uuid === detailState.store.uuid;
          const isSameBanner = ranking.store?.banner?.uuid === detailState.store.banner?.uuid;

          return {
            storeName: ranking.store?.name || 'Unknown Store',
            storeSubtext: isCurrentStore ? `Your Store (#${ranking.rank})` :
                         isSameBanner ? 'Same Banner' : 'Competitor',
            storeCode: ranking.store?.number ? `#${ranking.store.number}` : 'N/A',
            status: ranking.store?.getCurrentStatus?.()?.getStatusDisplayName?.() || 'Open',
            fit: ranking.store?.fit || 'N/A',
            salesPerSqft: ranking.volume?.salesSqftByTotalArea ? `$${ranking.volume.salesSqftByTotalArea.toLocaleString()}` : 'N/A',
            totalSalesArea: ranking.store?.grocerySalesArea ? `${ranking.store.grocerySalesArea.toLocaleString()} sq ft` : 'N/A',
            totalSalesVolume: ranking.value ? `$${ranking.value.toLocaleString()}` : 'N/A',
            isCurrentStore: isCurrentStore
          };
        });

        console.log("the stores are : - ",rows);

        return {
          currentStoreRank: currentStoreRanking?.rank || 0,
          totalStores: currentStoreRanking?.totalCount || rankings.length,
          rows: rows
        };
      }

      // Only use static data as fallback - no API calls
      console.log('No existing ranking data available, using static fallback');
      return this.getStaticStoreRankTableData();
    } catch (error) {
      console.error('Error building ranking table data:', error);
      return this.getStaticStoreRankTableData();
    }
  }

  /**
   * Fallback static data when API fails
   */
  private getStaticStoreRankTableData(): StoreRankTableData {
    // Static sample data as specified by user
    const sampleRows: StoreRankTableRow[] = [
      {
        storeName: 'Walmart SC',
        storeSubtext: 'Your Store (#15)',
        storeCode: '#3390',
        status: 'Open',
        fit: 'A',
        salesPerSqft: '$1,250',
        totalSalesArea: '2,500 sq ft',
        totalSalesVolume: '$3,125,000',
        isCurrentStore: true
      },
      {
        storeName: 'Target',
        storeSubtext: 'Same Banner',
        storeCode: '#4521',
        status: 'Open',
        fit: 'A',
        salesPerSqft: '$1,180',
        totalSalesArea: '3,200 sq ft',
        totalSalesVolume: '$3,776,000',
        isCurrentStore: false
      },
      {
        storeName: 'Kroger',
        storeSubtext: 'Competitor',
        storeCode: '#2100',
        status: 'Open',
        fit: 'B',
        salesPerSqft: '$950',
        totalSalesArea: '2,800 sq ft',
        totalSalesVolume: '$2,660,000',
        isCurrentStore: false
      },
      {
        storeName: 'Safeway',
        storeSubtext: 'Same Banner',
        storeCode: '#1890',
        status: 'Open',
        fit: 'A',
        salesPerSqft: '$1,100',
        totalSalesArea: '2,100 sq ft',
        totalSalesVolume: '$2,310,000',
        isCurrentStore: false
      },
      {
        storeName: 'Whole Foods',
        storeSubtext: 'Competitor',
        storeCode: '#5670',
        status: 'Open',
        fit: 'A',
        salesPerSqft: '$1,450',
        totalSalesArea: '1,900 sq ft',
        totalSalesVolume: '$2,755,000',
        isCurrentStore: false
      },
      {
        storeName: 'Trader Joes',
        storeSubtext: 'Competitor',
        storeCode: '#3210',
        status: 'Open',
        fit: 'B',
        salesPerSqft: '$1,320',
        totalSalesArea: '1,500 sq ft',
        totalSalesVolume: '$1,980,000',
        isCurrentStore: false
      },
      {
        storeName: 'Publix',
        storeSubtext: 'Same Banner',
        storeCode: '#7890',
        status: 'Open',
        fit: 'A',
        salesPerSqft: '$1,050',
        totalSalesArea: '2,600 sq ft',
        totalSalesVolume: '$2,730,000',
        isCurrentStore: false
      },
      {
        storeName: 'Costco',
        storeSubtext: 'Competitor',
        storeCode: '#4320',
        status: 'Open',
        fit: 'A',
        salesPerSqft: '$1,800',
        totalSalesArea: '5,000 sq ft',
        totalSalesVolume: '$9,000,000',
        isCurrentStore: false
      },
      {
        storeName: 'Aldi',
        storeSubtext: 'Competitor',
        storeCode: '#6540',
        status: 'Open',
        fit: 'C',
        salesPerSqft: '$780',
        totalSalesArea: '1,200 sq ft',
        totalSalesVolume: '$936,000',
        isCurrentStore: false
      },
      {
        storeName: 'HEB',
        storeSubtext: 'Same Banner',
        storeCode: '#8910',
        status: 'Open',
        fit: 'A',
        salesPerSqft: '$1,150',
        totalSalesArea: '3,500 sq ft',
        totalSalesVolume: '$4,025,000',
        isCurrentStore: false
      },


    ];

    return {
      currentStoreRank: 15,
      totalStores: 16,
      rows: sampleRows
    };
  }

  // async generateStoreDetailPdf(storeName: string = 'store-report'): Promise<void> {
  //   try {
  //     const pdf = new jsPDF('p', 'mm', 'a4');
  //     const pageWidth = pdf.internal.pageSize.getWidth();
  //     const pageHeight = pdf.internal.pageSize.getHeight();
  //     const margin = 10;
  //     const contentWidth = pageWidth - (2 * margin);

  //     // Page 1 - Critical Insights, Sales Performance, Additional Insights
  //     const page1Elements = [
  //       'mtn-store-header-panel',
  //       'mtn-store-alerts-panel',
  //       'mtn-store-critical-insights-panel',
  //       'mtn-store-sales-performance-panel',
  //       'mtn-store-additional-insights-panel'
  //     ];

  //     await this.addPageToPdf(pdf, page1Elements, margin, contentWidth, pageHeight, true);

  //     // Page 2 - Demographic Profile and Banner Analysis
  //     pdf.addPage();
  //     const page2Elements = [
  //       'mtn-store-header-panel',
  //       'mtn-store-demographic-profile-panel',
  //       'mtn-store-banner-analysis-panel'
  //     ];

  //     await this.addPageToPdf(pdf, page2Elements, margin, contentWidth, pageHeight, false);

  //     // Save the PDF
  //     const fileName = `${storeName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  //     pdf.save(fileName);
  //   } catch (error) {
  //     console.error('Error generating PDF:', error);
  //     throw error;
  //   }
  // }

  /**
   * Add elements to a PDF page
   */
//   private async addPageToPdf(
//     pdf: jsPDF,
//     elementSelectors: string[],
//     margin: number,
//     contentWidth: number,
//     pageHeight: number,
//     isFirstPage: boolean
//   ): Promise<void> {
//     let yOffset = margin;

//     for (const selector of elementSelectors) {
//       const element = document.querySelector(selector) as HTMLElement;

//       if (!element || element.offsetHeight === 0) {
//         continue;
//       }

//       // Skip alerts panel if it's hidden or has no alerts
//       if (selector === 'mtn-store-alerts-panel' && element.hidden) {
//         continue;
//       }

//       try {
//         const canvas = await html2canvas(element, {
//           scale: 2,
//           useCORS: true,
//           logging: false,
//           backgroundColor: '#ffffff'
//         });

//         const imgData = canvas.toDataURL('image/png');
//         const imgWidth = contentWidth;
//         const imgHeight = (canvas.height * imgWidth) / canvas.width;

//         // Check if we need to add a new page
//         if (yOffset + imgHeight > pageHeight - margin && !isFirstPage) {
//           pdf.addPage();
//           yOffset = margin;
//         }

//         pdf.addImage(imgData, 'PNG', margin, yOffset, imgWidth, imgHeight);
//         yOffset += imgHeight + 5; // Add small gap between sections
//       } catch (error) {
//         console.error(`Error capturing ${selector}:`, error);
//       }
//     }
//   }


// @ViewChild('pageContent', { static: false }) pageContent: ElementRef;  // 👈 reference to the whole page
// // 📌 NEW FUNCTION
//   exportPageAsPDF(): void {
//   const element = this.pageContent.nativeElement;

//   // 🔹 hide the toolbar (Download button)
//   const toolbar = element.querySelector('.actions-toolbar') as HTMLElement;
//   if (toolbar) {
//     toolbar.style.display = 'none';
//   }

//   const elementWidth = element.scrollWidth;
//   const elementHeight = element.scrollHeight;

//   html2canvas(element, {
//     scale: 2,
//     useCORS: true,
//     backgroundColor: '#050a1f',  // you can change to '#faf5e1' for yellow
//     width: elementWidth,
//     height: elementHeight,
//     windowWidth: elementWidth,
//     windowHeight: elementHeight,
//     scrollX: 0,
//     scrollY: 0
//   }).then(canvas => {
//     // 🔹 restore toolbar after capture
//     if (toolbar) {
//       toolbar.style.display = '';
//     }

//     const imgData = canvas.toDataURL('image/png');
//     const pdf = new jsPDF('p', 'mm', 'a4');

//     const pageWidth = pdf.internal.pageSize.getWidth();
//     const pageHeight = pdf.internal.pageSize.getHeight();

//     const imgWidth = pageWidth;
//     const imgHeight = (canvas.height * pageWidth) / canvas.width;

//     let heightLeft = imgHeight;
//     let position = 0;

//     while (heightLeft > 0) {
//       pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
//       heightLeft -= pageHeight;
//       position -= pageHeight;
//       if (heightLeft > 0) {
//         pdf.addPage();
//       }
//     }

//     pdf.save(`StoreDetail.pdf`);
//   });
// }


  // async generateStoreDetailPdfAlternative(storeName: string = 'store-report'): Promise<void> {
  //   try {
  //     const pdf = new jsPDF('p', 'mm', 'a4');
  //     const pageWidth = pdf.internal.pageSize.getWidth();
  //     const pageHeight = pdf.internal.pageSize.getHeight();
  //     const margin = 10;

  //     // Get the main content container
  //     const contentElement = document.getElementById('store-detail-content');

  //     if (!contentElement) {
  //       throw new Error('Content element not found');
  //     }

  //     // Hide elements we don't want in the PDF
  //     const elementsToHide = [
  //       '.actions-toolbar',
  //       'mtn-footer',
  //       'mtn-spinner'
  //     ];

  //     const hiddenElements: HTMLElement[] = [];
  //     elementsToHide.forEach(selector => {
  //       const elements = contentElement.querySelectorAll(selector);
  //       elements.forEach((el: HTMLElement) => {
  //         if (el.style.display !== 'none') {
  //           hiddenElements.push(el);
  //           el.style.display = 'none';
  //         }
  //       });
  //     });

  //     // Capture the entire content
  //     const canvas = await html2canvas(contentElement, {
  //       scale: 2,
  //       useCORS: true,
  //       logging: false,
  //       backgroundColor: '#ffffff',
  //       windowWidth: contentElement.scrollWidth,
  //       windowHeight: contentElement.scrollHeight
  //     });

  //     // Restore hidden elements
  //     hiddenElements.forEach(el => {
  //       el.style.display = '';
  //     });

  //     //const imgData = canvas.toDataURL('image/png');
  //     const imgWidth = pageWidth - (2 * margin);
  //     const imgHeight = (canvas.height * imgWidth) / canvas.width;

  //     const pageContentHeight = pageHeight - (2 * margin);
  //     const totalPages = Math.ceil(imgHeight / pageContentHeight);

  //     // Split the image across multiple pages
  //     for (let page = 0; page < totalPages; page++) {
  //       if (page > 0) {
  //         pdf.addPage();
  //       }

  //       const sourceY = page * pageContentHeight * (canvas.width / imgWidth);
  //       const sourceHeight = Math.min(
  //         pageContentHeight * (canvas.width / imgWidth),
  //         canvas.height - sourceY
  //       );

  //       // Create a temporary canvas for this page's content
  //       const pageCanvas = document.createElement('canvas');
  //       pageCanvas.width = canvas.width;
  //       pageCanvas.height = sourceHeight;
  //       const pageCtx = pageCanvas.getContext('2d');

  //       if (pageCtx) {
  //         pageCtx.drawImage(
  //           canvas,
  //           0, sourceY,
  //           canvas.width, sourceHeight,
  //           0, 0,
  //           canvas.width, sourceHeight
  //         );

  //         const pageImgData = pageCanvas.toDataURL('image/png');
  //         const pageImgHeight = (sourceHeight * imgWidth) / canvas.width;

  //         pdf.addImage(pageImgData, 'PNG', margin, margin, imgWidth, pageImgHeight);
  //       }
  //     }

  //     // Save the PDF
  //     const fileName = `${storeName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  //     pdf.save(fileName);
  //   } catch (error) {
  //     console.error('Error generating PDF:', error);
  //     throw error;
  //   }
  // }

  onShowComprehensiveRanking(): void {
    this.showComprehensiveRanking = true;
  }


  private loadStore(uuid: string, comprehensiveRankingOnly = false): void {
    if (!this.isLoading) {
      this.isLoading = true;
      this.resetPageState();
      this.storeDetailService.loadOne(uuid, comprehensiveRankingOnly)
        .pipe(finalize(() => this.isLoading = false))
        .subscribe();
    }
  }

  private resetPageState(): void {
    this.store = null;
    this.ngrxStore.dispatch(DetailResetAction());
  }

  private subscribeToDetailState(): void {
    this.addSubscription(
      selectDetailState(this.ngrxStore)
        .subscribe((state: DetailState) => {
          this.store = state.store;
          this.isMarketAreaLoaded = !!state.marketArea;

          // Update store status for PDF metrics
          if (this.store) {
            const status = this.store.getCurrentStatus?.();
            if (status) {
              this.cardTitleStatus = status.getStatusDisplayName?.() || status.getStatusSystemName?.() || 'Unknown';
            }
          }

          this.checkPdfDataReady();
        })
    );
  }

  private checkPdfDataReady(): void {
    const hasStore = !!this.store;
    const hasLicenseOrMarketArea = !this.hasLicense(LicenseType.STANDARD) || this.isMarketAreaLoaded;

    this.isPdfDataReady = hasStore && hasLicenseOrMarketArea && this.isCriticalInsightsUIReady;
    console.log('PDF Data Ready Check:', { hasStore, hasLicenseOrMarketArea, criticalInsightsReady: this.isCriticalInsightsUIReady, isPdfDataReady: this.isPdfDataReady });
  }



  private subscribeToRouteChanges(): void {
    // Combine params and url so we know the route mode BEFORE calling loadOne().
    // This prevents the full store-detail API cascade from firing on the
    // comprehensive-ranking route, where only store + volume are needed.
    this.addSubscription(
      combineLatest([this.route.params, this.route.url])
        .subscribe(([params, urlSegments]) => {
          const uuid: string = params['uuid'];
          const isComprehensiveRanking = urlSegments.some(
            segment => segment.path === 'comprehensive-ranking'
          );

          this.showComprehensiveRanking = isComprehensiveRanking;

          if (this.uuid !== uuid) {
            this.uuid = uuid;
            this.loadStore(uuid, isComprehensiveRanking);
          }
        })
    );
  }

  onCriticalInsightsReady(): void {
    this.isCriticalInsightsUIReady = true;
    this.checkPdfDataReady();
  }

}
